/**
 * Camada 0 · tema claro e escuro.
 *
 * O QUE ESTE PACOTE FAZ, e o que ele deliberadamente nao faz: ele decide QUAL tema esta
 * ligado e escreve isso no elemento raiz. Ele nao conhece cor nenhuma. A cor de cada tema
 * mora em @complexity/tokens/tokens.css, e trocar o tema aqui apenas redefine quais
 * valores de token valem.
 *
 * A escolha da pessoa vence a preferencia do sistema operacional. Quem clicou em "claro"
 * dentro do produto nao quer o produto escuro so porque o macOS esta escuro. Por isso a
 * preferencia do sistema so vale enquanto nao houve escolha explicita.
 */

import { ATRIBUTO_TEMA, TEMAS, type Tema } from '@complexity/tokens';

export type { Tema };
export { TEMAS };

/** Chave de armazenamento. Uma so, e exportada, para nenhum app inventar a propria e
 *  a preferencia se perder ao navegar entre produtos do mesmo dominio. */
export const CHAVE_TEMA = 'cx.tema';

/** Le a preferencia do sistema operacional. Vale so quando nao houve escolha explicita. */
export function temaDoSistema(): Tema {
  // matchMedia nao existe em renderizacao no servidor nem em teste sem DOM, entao a
  // ausencia dele nao pode lancar: o padrao honesto e o claro.
  if (typeof window === 'undefined' || !window.matchMedia) return 'light';
  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
}

/** Le o tema guardado, ou null quando a pessoa ainda nao escolheu. */
export function temaGuardado(): Tema | null {
  if (typeof window === 'undefined') return null;
  try {
    const bruto = window.localStorage.getItem(CHAVE_TEMA);
    // Valida contra a lista em vez de confiar: o armazenamento e editavel pelo usuario,
    // e um valor invalido ali nao pode virar um atributo invalido no documento.
    return TEMAS.includes(bruto as Tema) ? (bruto as Tema) : null;
  } catch {
    // Janela privada e navegador com armazenamento bloqueado LANCAM ao ler. Deixar
    // estourar aqui derrubaria a aplicacao inteira por causa de uma preferencia visual.
    return null;
  }
}

/**
 * O PADRAO DO PRODUTO, quando ele tem um.
 *
 * ---------------------------------------------------------------------------
 * POR QUE ISTO EXISTE, pedido dele em 07/09/2026: "DEFAUTO DO MEDICINA E TEMA CLARO"
 * ---------------------------------------------------------------------------
 * Ate aqui a ordem era: escolha da pessoa, e na falta dela o sistema operacional. Num
 * Windows em modo escuro, o medico-concierge abria ESCURO na primeira visita, e um
 * consultorio de alto padrao abre claro.
 *
 * ---------------------------------------------------------------------------
 * POR QUE UM VALOR DE MODULO, e nao um parametro em cada chamada
 * ---------------------------------------------------------------------------
 * `temaEfetivo` e chamada de tres lugares, e um deles e o `ControlesIdiomaTema`, que o
 * `Topbar` desenha. O `Topbar` esta CONGELADO pela regra do mobile do VOE, de
 * 06/09/2026: passar o padrao por prop obrigaria a edita-lo, e o congelamento existe
 * justamente para impedir que o ajuste de OUTRO produto desfaca o VOE sem ninguem
 * chegar a abrir o VOE.
 *
 * ---------------------------------------------------------------------------
 * O PRODUTO QUE NAO DECLARA CONTINUA EXATAMENTE COMO ESTAVA
 * ---------------------------------------------------------------------------
 * `null` e o valor inicial, e ai a ordem antiga vale inteira. O VOE, o medico-concierge e o
 * admin nunca chamam o ajuste abaixo: para eles nao muda o comportamento nem o retrato
 * congelado.
 *
 * A ESCOLHA DA PESSOA CONTINUA VENCENDO TUDO. Este padrao entra ANTES do sistema
 * operacional e DEPOIS do que ela escolheu: quem clicou em escuro dentro do produto nao
 * quer o produto claro so porque o produto prefere claro.
 */
let padraoDoProduto: Tema | null = null;

/**
 * Declara o tema com que o produto abre quando ninguem escolheu ainda.
 *
 * CHAMADA UMA VEZ, NO ARRANQUE DO APP, antes de qualquer tela desenhar: chamada depois,
 * a primeira pintura ja aconteceu e a tela pisca do tema errado para o certo.
 */
export function definirTemaPadraoDoProduto(tema: Tema): void {
  padraoDoProduto = tema;
  // Aplica na hora, e nao so na proxima leitura: quem chama esta no arranque, e o
  // documento ainda carrega o atributo do carregamento anterior.
  if (temaGuardado() === null) aplicarTema(tema);
}

/** O tema que vale agora: o escolhido, na falta dele o do PRODUTO, e na falta dos dois
 *  o do sistema operacional. */
export function temaEfetivo(): Tema {
  return temaGuardado() ?? padraoDoProduto ?? temaDoSistema();
}

/**
 * Aplica o tema ao documento. E o unico ponto do sistema que escreve o atributo,
 * para nao existirem dois lugares fazendo isso e divergindo.
 */
export function aplicarTema(tema: Tema): void {
  if (typeof document === 'undefined') return;
  document.documentElement.setAttribute(ATRIBUTO_TEMA, tema);
  // color-scheme faz o navegador pintar as barras de rolagem e os controles nativos no
  // tom certo. Sem isso, o campo de data nasce branco no meio de uma tela escura.
  document.documentElement.style.colorScheme = tema;
}

/** Guarda a escolha e aplica. A ordem importa: aplica mesmo que guardar falhe, porque
 *  a pessoa clicou e precisa ver a mudanca agora, ainda que ela nao sobreviva ao reload. */
export function definirTema(tema: Tema): void {
  aplicarTema(tema);
  try {
    window.localStorage.setItem(CHAVE_TEMA, tema);
  } catch {
    // Ignorado de proposito: ver comentario em temaGuardado.
  }
}

/** Alterna entre os dois. Usado pelo botao da topbar. */
export function alternarTema(): Tema {
  const proximo: Tema = temaEfetivo() === 'dark' ? 'light' : 'dark';
  definirTema(proximo);
  return proximo;
}

/**
 * Sincroniza o documento com a preferencia atual e passa a ouvir a do sistema.
 * Devolve a funcao que desfaz a escuta, para quem chamou poder limpar.
 *
 * A escuta so age enquanto NAO houve escolha explicita: depois que a pessoa escolheu,
 * mudar o tema do sistema operacional nao pode mais virar o produto embaixo dela.
 */
export function iniciarTema(): () => void {
  aplicarTema(temaEfetivo());

  if (typeof window === 'undefined' || !window.matchMedia) return () => {};

  const consulta = window.matchMedia('(prefers-color-scheme: dark)');
  const aoMudar = () => {
    if (temaGuardado() === null) aplicarTema(temaDoSistema());
  };
  consulta.addEventListener('change', aoMudar);
  return () => consulta.removeEventListener('change', aoMudar);
}
